/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';

import { RecordingStatus } from 'client-code/recordings-status';
import { FeedItem, FeedItemType, OfferType } from 'client-code/wrs-feed-service';

import DefaultTileImage from '../../../assets/images/1080p/hydra_image_default_empty_strip.png';
import { AppUtils } from '../../../core/AppUtils';
import { RootState, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { ImageUrlGenerator } from '../../../core/utils/ImageUrlGenerator';
import { getSingleChannelInfo } from '../../../store/homescreen/homescreen.reducers';
import { Mousetrap, ProgressBar } from '../../common';
import { RECORDING_TYPE, RecordingStatusIcon } from '../../Guide/RecordingStatusIcon';

const imageUrlGenerator = new ImageUrlGenerator();

/**
 * Calculates live content progress from startTime/endTime.
 * Returns a clamped 0–100 value, or null if inputs are invalid.
 */
const calculateLiveProgress = (startTime?: string, endTime?: string): number | null => {
    if (!startTime || !endTime) return null;
    const start = Date.parse(startTime);
    const end = Date.parse(endTime);
    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return null;
    const now = Date.now();
    const progress = ((now - start) / (end - start)) * 100;
    return Math.min(100, Math.max(0, progress));
};

interface TileProps {
    feedItem: FeedItem;
    isFocused: boolean;
    imageHostBaseUrl: string;
    isChannelThumbnail?: boolean;
    carouselIndex?: number;
    tileIndex?: number;
}

const Tile: FunctionComponent<TileProps> = ({
    feedItem,
    isFocused,
    imageHostBaseUrl,
    isChannelThumbnail,
    carouselIndex,
    tileIndex,
}): JSX.Element => {
    const [badgesVisible, setBadgesVisible] = useState(true);
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // When tile gains focus, start a 3 s timer to hide badges.
    // When it loses focus, cancel the timer and show badges again.
    useEffect(() => {
        if (isFocused) {
            hideTimerRef.current = setTimeout(() => {
                setBadgesVisible(false);
            }, 3000);
        } else {
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
            setBadgesVisible(true);
        }
        return () => {
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
        };
    }, [isFocused]);
    const tileClass = [
        'tile',
        isChannelThumbnail ? 'tile--channel' : '',
        feedItem.feedItemType === FeedItemType.Movie ? 'tile--movie' : '',
        isFocused ? 'tile--focused' : '',
    ]
        .filter(Boolean)
        .join(' ');

    const imageUrl = useMemo(() => {
        if (!imageHostBaseUrl) return '';

        // For recently watched channels carousel: resolve channel logo via stationId
        // regardless of feedItemType, covering all tile kinds in that row.
        if (isChannelThumbnail) {
            if (feedItem.stationId) {
                const channel = getSingleChannelInfo(feedItem.stationId);
                if (channel?.logoIndex) {
                    return imageUrlGenerator.buildImageUrlForHomeScreenChannelThumbnail(
                        imageHostBaseUrl,
                        channel.logoIndex,
                    );
                }
            }
            return '';
        }

        if (!feedItem.digItemId) return '';

        const imageType = imageUrlGenerator.getImageTypeFromFeedItemType(feedItem.feedItemType);
        return imageUrlGenerator.buildImageUrl(imageHostBaseUrl, feedItem.digItemId, imageType);
    }, [
        imageHostBaseUrl,
        feedItem.stationId,
        feedItem.digItemId,
        feedItem.feedItemType,
        isChannelThumbnail,
    ]);

    const [imageError, setImageError] = useState(false);

    const handleImageError = () => {
        setImageError(true);
    };

    const renderFallbackImage = () => (
        <div className="tile-fallback">
            <img className="tile-image" src={DefaultTileImage} alt={feedItem.title ?? ''} />
            {feedItem.title && <span className="tile-fallback-title">{feedItem.title}</span>}
        </div>
    );

    const renderImage = () => {
        if (isChannelThumbnail) {
            const callSign = feedItem.stationId
                ? getSingleChannelInfo(feedItem.stationId)?.callSign
                : feedItem.title;
            if (!imageUrl || imageError) {
                return (
                    <div className="tile-channel-logo-wrapper">
                        <span className="tile-channel-logo-fallback">{callSign}</span>
                    </div>
                );
            }
            return (
                <div className="tile-channel-logo-wrapper">
                    <img
                        className="tile-channel-logo"
                        src={imageUrl}
                        alt={callSign}
                        onError={handleImageError}
                    />
                </div>
            );
        }
        if (!imageUrl || imageError) {
            return renderFallbackImage();
        }
        return (
            <img
                className="tile-image"
                src={imageUrl}
                alt={feedItem.title}
                onError={handleImageError}
            />
        );
    };

    const recordingsStatusMap = useTivoSelector(
        (state: RootState) => state.recordingsStatus.recordingsStatusMap,
    );

    const getRecordingStatusType = (): string | null => {
        const { offerId, stationId } = feedItem;
        if (!offerId || !stationId) return null;
        const status = recordingsStatusMap.get(`${stationId}${offerId}`);
        if (!status) return null;
        switch (status) {
            case RecordingStatus.InProgressRecording:
                return RECORDING_TYPE.InProgressRecording;
            default:
                return null;
        }
    };

    const renderBadges = () => {
        const badges: JSX.Element[] = [];

        const recordingStatusType = getRecordingStatusType();
        if (recordingStatusType) {
            badges.push(
                <div key="recording-status" className="tile-badge tile-badge--recording-status">
                    <RecordingStatusIcon type={recordingStatusType} />
                </div>,
            );
        }

        if (feedItem.isNew) {
            badges.push(
                <div key="new" className="tile-badge tile-badge--new">
                    NEW
                </div>,
            );
        }

        if (feedItem.isLive) {
            badges.push(
                <div key="live" className="tile-badge tile-badge--live">
                    LIVE
                </div>,
            );
        }

        if (badges.length === 0) return null;

        const badgesClass = `tile-badges${isFocused && !badgesVisible ? ' tile-badges--hidden' : ''}`;

        return <div className={badgesClass}>{badges}</div>;
    };

    const renderProgress = () => {
        if (feedItem.offerType === OfferType.Live) {
            const progressPercent = calculateLiveProgress(feedItem.startTime, feedItem.endTime);
            if (progressPercent == null || progressPercent <= 0) return null;
            return (
                <div className="tile-progress">
                    <div className="tile-progress-inactive" />
                    <div
                        className="tile-progress-active"
                        style={{ width: `${progressPercent}%` }}
                    />
                </div>
            );
        }

        // Non-live (VOD, SoCu, Recording) - green progress bar with dot indicator
        if (feedItem.percentWatched != null && feedItem.percentWatched > 0) {
            return (
                <div className="tile-progress">
                    <ProgressBar percentage={feedItem.percentWatched} />
                </div>
            );
        }

        return null;
    };

    const mouseValue =
        carouselIndex != null && tileIndex != null
            ? AppUtils.createHighlightId(tileIndex, carouselIndex)
            : '';

    return (
        <Mousetrap value={mouseValue}>
            <div className={tileClass}>
                {renderImage()}
                {!isChannelThumbnail && renderBadges()}
                {!isChannelThumbnail && renderProgress()}
            </div>
        </Mousetrap>
    );
};

export default Tile;
